/**
 * The CASE page — the codename family as a place (/cases/{familyKey}).
 *
 * A HORIZONTAL chain bar under the header carries the members — ✉
 * Inbound, ▣ Requests by suffix, ⬒ Reports — most cases are short
 * (inbound → request → report), so a bar spends the space better than
 * a rail column. Below it: the selected node's full workspace (intake
 * or triage), embedded so the bar replaces their inline family strips.
 *
 * Selection is addressed by ?item=enq:{id}|rr:{id} (pushState — Back
 * walks the chain); ?openChat and ?section ride along untouched.
 * Default selection = the first node that needs the analyst.
 */

import { useCallback, useMemo } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Alert, Button, Card, Center, Group, Loader, Stack, Text } from '@mantine/core';

import * as Sentry from '@sentry/react';

import { CaseSharing } from '@/components/CaseSharing';
import { EditableCodename } from '@/components/EditableCodename';
import { CaseChain } from '@/components/TriageWorkspace/CaseChain';
import { CaseOwnerControl } from '@/components/TriageWorkspace/CaseOwnerControl';
import { usePoll } from '@/hooks/usePoll';
import { vnextApiClient } from '@/services/vnext-api';
import {
  CaseDetail,
  caseItemParam,
  CaseItemRef,
  caseShareAnchor,
  parseCaseItemParam,
} from '@/types/case';
import { toError } from '@/utils/errors';
import { EnquiryIntakeWorkspace } from './EnquiryDetail.page';
import { TriageRequestWorkspace } from './PendingTriageDetail.page';

/** Chain refresh cadence — cheap single GET; keeps the rail current
 * while the analyst works inside a node (e.g. after a convert). */
const CASE_POLL_MS = 15_000;

export function CasePage() {
  const { familyKey } = useParams<{ familyKey: string }>();
  const navigate = useNavigate();

  const poll = usePoll<CaseDetail>({
    enabled: familyKey !== undefined,
    interval: CASE_POLL_MS,
    refetchDeps: [familyKey],
    queryFn: async ({ signal }) => {
      if (!familyKey) {
        throw new Error('familyKey is required');
      }
      return vnextApiClient.getCase(familyKey, { signal });
    },
    onError: (err) => Sentry.captureException(toError(err)),
  });
  const caseDetail = poll.data ?? null;
  const error =
    poll.error === undefined ? null : toError(poll.error, 'Failed to load case').message;
  const { refetch } = poll;

  // A rename changes the family key — follow it to the new URL.
  const handleRenamed = useCallback(
    (newKey: string) => {
      navigate(`/cases/${encodeURIComponent(newKey)}${window.location.search}`, {
        replace: true,
      });
    },
    [navigate]
  );

  // Nothing loaded yet, so there is no workspace to keep on screen.
  if (error && !caseDetail) {
    return (
      <Center h={300}>
        <Stack align="center" gap="xs">
          <Text c="red">{error}</Text>
          <Button variant="subtle" size="xs" onClick={() => navigate('/inbox')}>
            ← Back to inbox
          </Button>
        </Stack>
      </Center>
    );
  }

  if (!caseDetail) {
    return (
      <Center h={300}>
        <Loader />
      </Center>
    );
  }

  return (
    <Stack gap={0}>
      {/* A failed refresh must not tear the workspace down: its sections hold
          the analyst's unsaved edits, and the next poll usually recovers. */}
      {error && (
        <Alert
          color="yellow"
          title="Couldn't refresh this case"
          m="md"
          mb={0}
          data-testid="case-stale-banner"
        >
          <Group justify="space-between" wrap="wrap">
            <Text size="sm">
              {error}. Showing the last version loaded; if the case was renamed or removed, go back
              to the inbox to find it.
            </Text>
            <Group gap="xs">
              <Button size="xs" variant="light" onClick={() => void refetch()}>
                Retry
              </Button>
              <Button size="xs" variant="subtle" onClick={() => navigate('/inbox')}>
                ← Back to inbox
              </Button>
            </Group>
          </Group>
        </Alert>
      )}
      <CaseWorkspace caseDetail={caseDetail} onRenamed={handleRenamed} onChanged={refetch} />
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Workspace: rail + selected node
// ---------------------------------------------------------------------------

/** First node that needs the analyst: an unfinished request, else an
 * unconverted intake, else the first request, else the intake. */
function defaultItem(caseDetail: CaseDetail): CaseItemRef | null {
  const openRequest = caseDetail.reportRequests.find(
    (rr) => rr.triageStage !== 'started' && rr.triageStage !== 'rejected'
  );
  if (openRequest) {
    return { type: 'report_request', id: openRequest.id };
  }
  if (caseDetail.enquiry && caseDetail.enquiry.stage === 'received') {
    return { type: 'enquiry', id: caseDetail.enquiry.id };
  }
  if (caseDetail.reportRequests.length > 0) {
    return { type: 'report_request', id: caseDetail.reportRequests[0].id };
  }
  if (caseDetail.enquiry) {
    return { type: 'enquiry', id: caseDetail.enquiry.id };
  }
  return null;
}

function CaseWorkspace({
  caseDetail,
  onRenamed,
  onChanged,
}: Readonly<{
  caseDetail: CaseDetail;
  onRenamed: (newKey: string) => void;
  onChanged: () => Promise<void>;
}>) {
  const [searchParams, setSearchParams] = useSearchParams();
  const selected = parseCaseItemParam(searchParams.get('item')) ?? defaultItem(caseDetail);

  const select = useCallback(
    (ref: CaseItemRef) => {
      const next = new URLSearchParams(searchParams);
      next.set('item', caseItemParam(ref));
      // A fresh node selection must not inherit the previous node's
      // expanded-section state.
      next.delete('section');
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  const rollup = useMemo(() => {
    const parts: string[] = [];
    if (caseDetail.enquiry) {
      parts.push(`enquiry ${caseDetail.enquiry.stage}`);
    }
    if (caseDetail.reportRequests.length > 0) {
      parts.push(
        `${caseDetail.reportRequests.length} request${
          caseDetail.reportRequests.length === 1 ? '' : 's'
        }`
      );
    }
    const reportCount =
      caseDetail.reports.length +
      caseDetail.reportRequests.filter((rr) => rr.reportId && rr.triageStage === 'started').length;
    if (reportCount > 0) {
      parts.push(`${reportCount} report${reportCount === 1 ? '' : 's'}`);
    }
    return parts.join(' · ');
  }, [caseDetail]);

  const shareAnchor = caseShareAnchor(caseDetail);

  return (
    <Stack gap="md" px="md" py="md">
      <Group justify="space-between" wrap="wrap">
        <Group gap="sm">
          <EditableCodename
            value={caseDetail.familyKey}
            size="md"
            fw={700}
            onSaved={(newKey) => onRenamed(newKey)}
            data-testid="case-codename"
          />
          <Text size="sm" c="dimmed">
            {rollup}
          </Text>
          <CaseOwnerControl caseDetail={caseDetail} onChanged={onChanged} />
        </Group>
        <Button variant="subtle" size="xs" component={Link} to="/inbox">
          ← Back to inbox
        </Button>
      </Group>

      {/* Shared at the root so the whole family follows; the embedded
          workspaces below leave their own control out. */}
      {shareAnchor && <CaseSharing anchor={shareAnchor} />}

      <CaseChain caseDetail={caseDetail} current={selected} onSelect={select} />

      {/* key= forces a remount when the chain selection moves to a different
          node — child sections seed local state from props, so re-propping
          the same instance leaks e.g. RR1's subject text into RR2. */}
      {selected?.type === 'enquiry' && (
        <EnquiryIntakeWorkspace
          key={selected.id}
          enquiryId={selected.id}
          embedded
          onFamilyChanged={onChanged}
          onFamilyRenamed={onRenamed}
        />
      )}
      {selected?.type === 'report_request' && (
        <TriageRequestWorkspace
          key={selected.id}
          requestId={selected.id}
          embedded
          onFamilyChanged={onChanged}
          onFamilyRenamed={onRenamed}
        />
      )}
      {!selected && (
        <Card shadow="sm" padding="lg" radius="md">
          <Text size="sm" c="dimmed">
            This case holds self-served reports only — open one from the chain above.
          </Text>
        </Card>
      )}
    </Stack>
  );
}
